/**
 * CADChatPanel - Lightweight ephemeral chat for CAD model analysis
 *
 * Creates a thread on first message, streams responses via SSE.
 * Resets on model change or unmount (no persistent history).
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Typography,
  CircularProgress,
  Paper,
} from '@mui/material';
import {
  Send as SendIcon,
  SmartToy as AIIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import chatService from '../../services/chatService';
import { CADMarker } from '../../types/cad';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

interface CADChatPanelProps {
  modelId: string | null;
  modelName?: string;
  onMarkersUpdate?: (markers: CADMarker[]) => void;
  comparisonModelId?: string;
  comparisonModelName?: string;
}

/**
 * Parse [point:x,y,z:label:type] markers from LLM response text.
 * Only matches complete tags (closed bracket). Deduplicates. Caps at 5.
 */
// Matches [point:...], [pointA:...], [pointB:...] markers (capture group 1 = A|B|undefined)
// eslint-disable-next-line no-useless-escape
const MARKER_REGEX = /\[point([AB])?:(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?):([^:\[\]]+)(?::(info|warning|error))?\]/g;

function parseMarkers(text: string): CADMarker[] {
  const regex = new RegExp(MARKER_REGEX.source, 'g');
  const markers: CADMarker[] = [];
  const seen = new Set<string>();
  let match;
  while ((match = regex.exec(text)) !== null) {
    const side = match[1] as 'A' | 'B' | undefined;
    const x = parseFloat(match[2]), y = parseFloat(match[3]), z = parseFloat(match[4]);
    if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) continue;
    const label = match[5].trim();
    if (!label) continue;
    const type = (match[6] as CADMarker['type']) || 'info';
    const key = `${x},${y},${z},${label},${type}`;
    if (seen.has(key)) continue;
    seen.add(key);
    markers.push({ position: [x, y, z], label, type, side });
  }
  return markers.slice(0, 5);
}

/**
 * Replace marker syntax with pin badge for display.
 */
function stripMarkerSyntax(text: string): string {
  // Replace [point:...] with 📍label — reuse shared regex source
  return text.replace(new RegExp(MARKER_REGEX.source, 'g'), '\uD83D\uDCCD$4');
}

function markersEqual(a: CADMarker[], b: CADMarker[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((m, i) =>
    m.position[0] === b[i].position[0] &&
    m.position[1] === b[i].position[1] &&
    m.position[2] === b[i].position[2] &&
    m.label === b[i].label &&
    m.type === b[i].type &&
    m.side === b[i].side,
  );
}

/**
 * Simple Markdown-to-JSX renderer for chat messages.
 * Handles headers, lists, bold, code, and blockquotes.
 */
function renderMarkdown(text: string): React.ReactNode {
  const lines = text.split('\n');
  const elements: React.ReactNode[] = [];

  lines.forEach((line, i) => {
    const trimmed = line.trim();

    if (trimmed.startsWith('### ')) {
      elements.push(
        <Typography key={i} variant="subtitle2" sx={{ mt: 1, mb: 0.5, fontWeight: 700, fontSize: '0.8rem' }}>
          {trimmed.slice(4)}
        </Typography>
      );
    } else if (trimmed.startsWith('## ')) {
      elements.push(
        <Typography key={i} variant="subtitle2" sx={{ mt: 1.5, mb: 0.5, fontWeight: 700, fontSize: '0.85rem' }}>
          {trimmed.slice(3)}
        </Typography>
      );
    } else if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
      elements.push(
        <Typography key={i} variant="body2" sx={{ pl: 1.5, fontSize: '0.78rem', lineHeight: 1.5 }}>
          {'• '}{formatInline(trimmed.slice(2))}
        </Typography>
      );
    } else if (/^\d+\.\s/.test(trimmed)) {
      const match = trimmed.match(/^(\d+)\.\s(.*)$/);
      if (match) {
        elements.push(
          <Typography key={i} variant="body2" sx={{ pl: 1.5, fontSize: '0.78rem', lineHeight: 1.5 }}>
            {match[1]}. {formatInline(match[2])}
          </Typography>
        );
      }
    } else if (trimmed.startsWith('> ')) {
      elements.push(
        <Box key={i} sx={{ borderLeft: '2px solid', borderColor: 'warning.main', pl: 1, my: 0.5, bgcolor: 'action.hover', borderRadius: '0 4px 4px 0' }}>
          <Typography variant="caption">{trimmed.slice(2)}</Typography>
        </Box>
      );
    } else if (trimmed === '') {
      elements.push(<Box key={i} sx={{ height: 3 }} />);
    } else {
      elements.push(
        <Typography key={i} variant="body2" sx={{ fontSize: '0.78rem', lineHeight: 1.5 }}>
          {formatInline(trimmed)}
        </Typography>
      );
    }
  });

  return <>{elements}</>;
}

function formatInline(text: string): React.ReactNode {
  const parts: React.ReactNode[] = [];
  let remaining = text;
  let key = 0;

  while (remaining.length > 0) {
    const boldMatch = remaining.match(/^(.*?)\*\*(.+?)\*\*(.*)/s);
    if (boldMatch) {
      if (boldMatch[1]) parts.push(<span key={key++}>{boldMatch[1]}</span>);
      parts.push(<strong key={key++}>{boldMatch[2]}</strong>);
      remaining = boldMatch[3];
      continue;
    }

    const codeMatch = remaining.match(/^(.*?)`(.+?)`(.*)/s);
    if (codeMatch) {
      if (codeMatch[1]) parts.push(<span key={key++}>{codeMatch[1]}</span>);
      parts.push(
        <code key={key++} style={{ backgroundColor: 'rgba(0,0,0,0.08)', padding: '1px 3px', borderRadius: 2, fontSize: '0.72rem' }}>
          {codeMatch[2]}
        </code>
      );
      remaining = codeMatch[3];
      continue;
    }

    parts.push(<span key={key++}>{remaining}</span>);
    break;
  }

  return <>{parts}</>;
}

const CADChatPanel: React.FC<CADChatPanelProps> = ({ modelId, modelName, onMarkersUpdate, comparisonModelId, comparisonModelName }) => {
  const { t } = useTranslation();
  const isCompareMode = Boolean(comparisonModelId);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [threadId, setThreadId] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const streamIdRef = useRef(0);
  const lastMarkersRef = useRef<CADMarker[]>([]);
  const onMarkersUpdateRef = useRef(onMarkersUpdate);
  onMarkersUpdateRef.current = onMarkersUpdate;

  // Reset on model change or comparison mode change
  useEffect(() => {
    streamIdRef.current += 1;
    setMessages([]);
    setInput('');
    setThreadId(null);
    setLoading(false);
    lastMarkersRef.current = [];
    onMarkersUpdateRef.current?.([]);
  }, [modelId, comparisonModelId]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = useCallback(async () => {
    if (!input.trim() || !modelId || loading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);
    const currentStreamId = ++streamIdRef.current;

    try {
      // Create thread lazily on first message
      let currentThreadId = threadId;
      if (!currentThreadId) {
        const title = isCompareMode
          ? t('cad.chat.threadCompareTitle', {
              modelA: modelName || '?',
              modelB: comparisonModelName || '?',
            })
          : t('cad.chat.threadTitle', { model: modelName || modelId.slice(0, 8) });
        const thread = await chatService.createThread(title);
        currentThreadId = thread.id;
        setThreadId(currentThreadId);
      }

      // Add empty assistant message for streaming
      setMessages((prev) => [...prev, { role: 'assistant', content: '' }]);

      await chatService.streamMessage(
        currentThreadId,
        userMessage,
        (content) => {
          if (streamIdRef.current !== currentStreamId) return;
          setMessages((prev) => {
            const updated = [...prev];
            updated[updated.length - 1] = { role: 'assistant', content };
            return updated;
          });
          // Parse and emit markers (only if changed)
          const newMarkers = parseMarkers(content);
          if (!markersEqual(newMarkers, lastMarkersRef.current)) {
            lastMarkersRef.current = newMarkers;
            onMarkersUpdateRef.current?.(newMarkers);
          }
        },
        undefined, // onSearchResults - not used for CAD mode
        undefined, // onComplete
        isCompareMode
          ? {
              mode: 'cad_compare',
              modeData: { model_id_a: modelId, model_id_b: comparisonModelId },
            }
          : {
              mode: 'cad',
              modeData: { model_id: modelId },
            }
      );
    } catch (err: any) {
      if (streamIdRef.current === currentStreamId) {
        setMessages((prev) => {
          const updated = [...prev];
          const last = updated[updated.length - 1];
          if (last?.role === 'assistant' && !last.content) {
            updated[updated.length - 1] = {
              role: 'assistant',
              content: t('cad.chat.error'),
            };
          }
          return updated;
        });
      }
    } finally {
      setLoading(false);
    }
  }, [input, modelId, modelName, loading, threadId, isCompareMode, comparisonModelId, comparisonModelName, t]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!modelId) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography color="text.secondary" variant="body2">
          {t('cad.chat.selectModel')}
        </Typography>
      </Box>
    );
  }

  const sampleQuestions = [
    t('cad.chat.sampleSize'),
    t('cad.chat.sampleManufacturing'),
    t('cad.chat.sampleQuality'),
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* Messages area */}
      <Box sx={{ flex: 1, overflow: 'auto', px: 1, py: 0.5 }}>
        {messages.length === 0 && (
          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Typography variant="caption" color="text.secondary">
              {t('cad.chat.guidance')}
            </Typography>
            <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {sampleQuestions.map((q) => (
                <Paper
                  key={q}
                  variant="outlined"
                  sx={{
                    px: 1, py: 0.5, cursor: 'pointer', fontSize: '0.75rem',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                  onClick={() => { setInput(q); }}
                >
                  {q}
                </Paper>
              ))}
            </Box>
          </Box>
        )}

        {messages.map((msg, i) => (
          <Box
            key={i}
            sx={{
              display: 'flex',
              gap: 0.5,
              mb: 1,
              alignItems: 'flex-start',
            }}
          >
            <Box sx={{ mt: 0.3, flexShrink: 0 }}>
              {msg.role === 'user' ? (
                <PersonIcon sx={{ fontSize: 16, color: 'primary.main' }} />
              ) : (
                <AIIcon sx={{ fontSize: 16, color: 'secondary.main' }} />
              )}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {msg.role === 'user' ? (
                <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 500 }}>
                  {msg.content}
                </Typography>
              ) : msg.content ? (
                renderMarkdown(stripMarkerSyntax(msg.content))
              ) : (
                <CircularProgress size={14} sx={{ mt: 0.5 }} />
              )}
            </Box>
          </Box>
        ))}
        <div ref={messagesEndRef} />
      </Box>

      {/* Input area */}
      <Box sx={{ display: 'flex', gap: 0.5, p: 0.5, borderTop: '1px solid', borderColor: 'divider' }}>
        <TextField
          size="small"
          fullWidth
          placeholder={t('cad.chat.placeholder')}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={loading}
          multiline
          maxRows={3}
          sx={{
            '& .MuiInputBase-input': { fontSize: '0.8rem', py: 0.5 },
          }}
        />
        <IconButton
          size="small"
          color="primary"
          onClick={handleSend}
          disabled={!input.trim() || loading}
        >
          {loading ? <CircularProgress size={18} /> : <SendIcon fontSize="small" />}
        </IconButton>
      </Box>
    </Box>
  );
};

export default CADChatPanel;
